import { createApp } from "vue";
import * as Sentry from "@sentry/vue";
import App from "./App.vue";
import router from "./router";
import type { FirebaseOptions } from "firebase/app";
import { initializeApp } from "firebase/app";
import {
	browserLocalPersistence,
	connectAuthEmulator,
	getAuth,
	setPersistence,
} from "firebase/auth";
import { createPinia } from "pinia";
import {
	defaultConfig as defaultFormkitConfig,
	plugin as formkitPlugin,
} from "@formkit/vue";
import { formkitConfig } from "./formkit.config";
import posthogPlugin from "./plugins/posthog.js";
import FloatingVue from "floating-vue";
import { useFavicon } from "@vueuse/core";
// @ts-expect-error - vue-virtual-scroller is not typed
import { RecycleScroller } from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";

async function main() {
	window.addEventListener("vite:preloadError", (event) => {
		event.preventDefault();
		window.location.reload();
	});

	const firebaseConfig: FirebaseOptions = {
		apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
		projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
		authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
		appId: import.meta.env.VITE_FIREBASE_APP_ID,
	};

	// Initialize Firebase
	initializeApp(firebaseConfig);

	const auth = getAuth();

	if (import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL) {
		connectAuthEmulator(auth, import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL, {
			disableWarnings: true,
		});
	}

	await setPersistence(auth, browserLocalPersistence);

	const { setIdToken } = await import("@/composables/useZero");
	auth.onIdTokenChanged(async (user) => {
		if (user) {
			const { token, claims } = await user.getIdTokenResult();
			setIdToken(token, claims);
		} else {
			setIdToken();
		}
	});

	const app = createApp(App);
	app.config.errorHandler = (err, vm, info) => {
		// eslint-disable-next-line no-console
		console.error("Error:", err);
		// eslint-disable-next-line no-console
		console.error("Vue component:", vm);
		// eslint-disable-next-line no-console
		console.error("Additional info:", info);
	};
	app.use(posthogPlugin);

	// Session Replay PII-maskering (SPEC-090, AVG art. 5 lid 2 — verantwoordingsplicht).
	// LoanLink verwerkt bijzondere/gewone persoonsgegevens (NAW, geboortedatum, inkomen,
	// werkgever, BSN-achtige velden) op vrijwel elke dossier-/inventarisatie-route. Sentry
	// treedt op als (sub)verwerker; onmaskeerde replays zouden complete klantdossiers in
	// plaintext naar Sentry sturen. Daarom gekozen voor de fail-safe strategie (Optie A):
	// álle tekst, álle invoervelden en alle media globaal maskeren. Zo zijn nieuwe PII-routes
	// automatisch gedekt (dataminimalisatie als waarborg) zonder per-route onderhoud, waarbij
	// de UI-structuur in replays zichtbaar blijft voor het debuggen van flows.
	// Afbakening: dit dekt de replay-DOM. Naam/e-mail van de INGELOGDE gebruiker gaan los
	// hiervan via Sentry.setUser (useUserStore.ts) + feedbackIntegration.useSentryUser naar
	// Sentry — dat betreft de eigen gebruiker (niet klantdossiers) en blijft bewust behouden
	// voor debug-identificatie. Een eventuele afbouw daarvan is een aparte afweging.
	Sentry.init({
		app,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		tunnel: `${import.meta.env.VITE_API_BASE_URL}/tunnel`,
		enableLogs: true,
		integrations: [
			Sentry.browserProfilingIntegration(),
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				maskAllText: true,
				maskAllInputs: true,
				blockAllMedia: true,
			}),
			Sentry.feedbackIntegration({
				autoInject: false,
				colorScheme: "light",
				triggerLabel: "Geef feedback",
				triggerAriaLabel: "Geef feedback",
				formTitle: "Feedback",
				messageLabel: "Omschrijving",
				isRequiredLabel: "(verplicht)",
				messagePlaceholder: "Wat kunnen we verbeteren?",
				successMessageText: "Bedankt voor je feedback, je hoort van ons",
				addScreenshotButtonLabel: "Screenshot toevoegen",
				removeScreenshotButtonLabel: "Screenshot verwijderen",
				submitButtonLabel: "Feedback versturen",
				cancelButtonLabel: "Annuleren",
				confirmButtonLabel: "Bevestigen",
				showName: false,
				showEmail: false,
				showBranding: false,
				useSentryUser: {
					name: "fullName",
					email: "email",
				},
			}),
		],
		tracesSampleRate: 1.0,
		replaysSessionSampleRate: 1.0,
		tracePropagationTargets: [import.meta.env.VITE_API_BASE_URL],
	});

	const pinia = createPinia();
	app.use(pinia);

	app.use(router);

	app.use(FloatingVue);
	app.use(formkitPlugin, defaultFormkitConfig(formkitConfig));
	app.component("RecycleScroller", RecycleScroller);

	if (import.meta.env.VITE_STAGE === "true") {
		const icon = useFavicon();
		icon.value = "/icon-stage.svg";
	}

	app.mount("#app");
}

void main();
